<template>
  <Modal
    size="none"
    :is-full-height="false"
    :show-close-button="false"
    inner-class="max-h-[90vh] overflow-y-auto"
    custom-class="w-full max-w-sm sm:max-w-md lg:max-w-3xl rounded-none bg-white"
    @close="onClose"
  >
    <!-- Custom close button -->
    <button
      class="hover:text-gray-600 absolute right-[22px] top-[22px] z-50 text-black"
      style="font-weight: bold"
      @click="onClose"
    >
      ✕
    </button>

    <div
      class="lg:flex lg:min-h-[480px]"
      role="dialog"
      aria-labelledby="choose-size-title"
      aria-describedby="choose-size-description"
    >
      <!-- Mobile -->
      <div class="flex flex-col p-3 lg:hidden">
        <div class="mb-3 flex items-center gap-3">
          <div class="shrink-0">
            <ProductImage
              v-if="productImage"
              :image="productImage"
              class="size-24 object-cover"
              sizes="96px"
            />
          </div>
          <div class="flex-1">
            <p
              id="choose-size-description"
              class="mb-2 pr-12 text-left text-lg font-[700]"
            >
              {{ $t('basket.choose_one_size.description') }}
            </p>
          </div>
        </div>

        <div v-if="sizeNote" class="mb-3 bg-platin p-3">
          <div class="flex items-center gap-3">
            <div class="mt-0.5 shrink-0">
              <IconMeasuringTape class="size-16" />
            </div>
            <div class="flex-1 text-left">
              <p class="text-base font-medium">{{ $t(sizeNote) }}</p>
            </div>
          </div>
        </div>

        <div class="mb-3 flex-1">
          <div
            class="space-y-3"
            role="radiogroup"
            aria-labelledby="choose-size-description"
          >
            <label
              v-for="item in basketItems"
              :key="item.variant.id"
              class="flex cursor-pointer items-center gap-3 p-2"
            >
              <input
                v-model="selectedVariantId"
                type="radio"
                :value="item.variant.id"
                class="radio-button"
                :aria-describedby="`size-${item.variant.id}-description`"
              />
              <div class="flex flex-1 items-center justify-between">
                <span
                  :id="`size-${item.variant.id}-description`"
                  class="text-base font-medium"
                >
                  {{
                    $t('basket.choose_one_size.order_size', {
                      size: getSizeDisplayText(item.variant),
                    })
                  }}
                </span>
              </div>
            </label>

            <label class="flex cursor-pointer items-center gap-3 p-2">
              <input
                v-model="selectedVariantId"
                type="radio"
                value="keep_all"
                class="radio-button"
                aria-describedby="keep-all-description"
              />
              <span id="keep-all-description" class="text-base font-medium">
                {{ $t('basket.choose_one_size.keep_all') }}
              </span>
            </label>
          </div>
        </div>

        <div class="mt-[22px]">
          <Button
            type="primary"
            class="w-full py-3 text-lg"
            :disabled="!selectedVariantId"
            ignore-capitalisation
            @click="onConfirm"
          >
            {{ $t('basket.choose_one_size.confirm') }}
          </Button>
        </div>
      </div>

      <!-- Desktop -->
      <div class="bg-gray-50 hidden w-[354px] shrink-0 p-[22px] lg:block">
        <ProductImage
          v-if="productImage"
          :image="productImage"
          class="size-full rounded object-cover"
          sizes="276px"
          style="min-width: 100%; min-height: 100%"
        />
      </div>

      <div class="hidden min-w-0 flex-1 flex-col p-[22px] pl-0 lg:flex">
        <div class="mb-[22px]">
          <p
            id="choose-size-description"
            class="pr-24 text-left text-lg font-[700]"
          >
            {{ $t('basket.choose_one_size.description') }}
          </p>
        </div>

        <div v-if="sizeNote" class="mb-[22px] bg-platin p-[22px]">
          <div class="flex items-center gap-[22px]">
            <div class="mt-0.5 shrink-0">
              <IconMeasuringTape class="size-16" />
            </div>
            <div class="flex-1">
              <p class="text-base font-medium">{{ $t(sizeNote) }}</p>
            </div>
          </div>
        </div>

        <div class="mb-[22px] flex-1">
          <div
            class="space-y-4"
            role="radiogroup"
            aria-labelledby="choose-size-description"
          >
            <label
              v-for="item in basketItems"
              :key="item.variant.id"
              class="flex cursor-pointer items-center gap-3 p-2"
            >
              <input
                v-model="selectedVariantId"
                type="radio"
                :value="item.variant.id"
                class="radio-button"
                :aria-describedby="`size-${item.variant.id}-description`"
              />
              <div class="flex flex-1 items-center justify-between">
                <span
                  :id="`size-${item.variant.id}-description`"
                  class="text-base font-medium"
                >
                  {{
                    $t('basket.choose_one_size.order_size', {
                      size: getSizeDisplayText(item.variant),
                    })
                  }}
                </span>
              </div>
            </label>

            <label class="flex cursor-pointer items-center gap-3 p-2">
              <input
                v-model="selectedVariantId"
                type="radio"
                value="keep_all"
                class="radio-button"
                aria-describedby="keep-all-description"
              />
              <span id="keep-all-description" class="text-base font-medium">
                {{ $t('basket.choose_one_size.keep_all') }}
              </span>
            </label>
          </div>
        </div>

        <Button
          type="primary"
          class="bg-gray-300 hover:bg-gray-400 text-gray-800 w-full py-[10px] text-[19px]"
          :disabled="!selectedVariantId"
          ignore-capitalisation
          @click="onConfirm"
        >
          {{ $t('basket.choose_one_size.confirm') }}
        </Button>
      </div>
    </div>
  </Modal>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import type { BasketItem, Product, Variant } from '@scayle/storefront-nuxt'
import { getFrontImage } from '#layers/base/utils/image'
import useSizeDisplayData from '#layers/base/composables/useSizeDisplayData'
import { getSizeNote } from '~/utils/product'

const { basketItems, product } = defineProps<{
  basketItems: BasketItem[]
  product: Product
}>()

const emit = defineEmits<{
  close: []
  confirm: [
    selection: { type: 'single'; variantId: number } | { type: 'keep_all' },
  ]
}>()

const sizeNote = computed(() => getSizeNote(product, true))

const { getSizeDisplayData } = useSizeDisplayData()

const selectedVariantId = ref<number | 'keep_all' | null>(null)

// Product info from the first item (they should all be the same product)
const firstItem = computed(() => basketItems[0])
const productImage = computed(() => {
  const item = firstItem.value
  return item ? getFrontImage(item.product.images) : undefined
})

const getSizeDisplayText = (variant: Variant): string => {
  const sizeData = getSizeDisplayData(variant)
  return typeof sizeData === 'object'
    ? sizeData.translatedValue || ''
    : sizeData || ''
}

const onClose = () => emit('close')

const onConfirm = () => {
  if (selectedVariantId.value === 'keep_all') {
    emit('confirm', { type: 'keep_all' })
  } else if (selectedVariantId.value) {
    emit('confirm', { type: 'single', variantId: selectedVariantId.value })
  }
}
</script>
